import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { fuseAnimations } from '@mobileia/layout-fuse';
import { SynonymService } from 'src/app/services/synonym.service';
import { AlertService } from 'src/app/services/alert.service';
import { Synonym, SynonymGroup } from 'src/app/entities/synonym';
import { ApiPagination } from '@mobileia/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-synonym-group',
  templateUrl: './synonym-group.component.html',
  styleUrls: ['./synonym-group.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: fuseAnimations
})
export class SynonymGroupComponent implements OnInit {

  isLoading = true;
  isSaving = false;
  dataItems = new ApiPagination<SynonymGroup>();
  searchQuery = '';
  searchSubject = new Subject<string>();
  currentPage = 1;
  perPage = 12;

  // Modal states
  showCreateModal = false;
  showEditModal = false;
  showDeleteModal = false;

  // Form data
  newGroupName = '';
  newGroupIsActive = 1;
  editingGroup: SynonymGroup | null = null;
  deletingGroup: SynonymGroup | null = null;

  // Inline synonym adding
  newSynonymPhrase: { [groupId: number]: string } = {};
  addingSynonym: { [groupId: number]: boolean } = {};

  // Expanded cards for managing synonyms
  expandedGroups: { [groupId: number]: boolean } = {};

  constructor(
    private synonymService: SynonymService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.loadGroups();
    
    // Setup debounced search
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(query => {
      this.searchQuery = query;
      this.currentPage = 1;
      this.loadGroups();
    });
  }

  loadGroups(): void {
    this.isLoading = true;
    this.synonymService.listGroups({
      page: this.currentPage,
      per_page: this.perPage,
      search: this.searchQuery || undefined
    }).subscribe(
      response => {
        this.isLoading = false;
        if (response.success) {
          this.dataItems = response.response;
        } else {
          this.alertService.showError('Error', 'No se pudieron cargar los grupos');
        }
      },
      error => {
        this.isLoading = false;
        this.alertService.showError('Error', 'Error de conexión');
      }
    );
  }

  onSearch(query: string): void {
    this.searchSubject.next(query);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.searchSubject.next('');
  }

  // ============ Create Group ============

  openCreateModal(): void {
    this.newGroupName = '';
    this.newGroupIsActive = 1;
    this.showCreateModal = true;
  }

  closeCreateModal(): void {
    this.showCreateModal = false;
    this.newGroupName = '';
    this.newGroupIsActive = 1;
  }

  createGroup(): void {
    if (!this.newGroupName.trim()) {
      this.alertService.showError('Error', 'El nombre del grupo es requerido');
      return;
    }

    this.isSaving = true;
    this.synonymService.saveGroup({
      group_name: this.newGroupName.trim(),
      is_active: this.newGroupIsActive
    }).subscribe(
      response => {
        this.isSaving = false;
        if (response.success) {
          this.alertService.showSuccess('Éxito', 'Grupo creado correctamente');
          this.closeCreateModal();
          this.loadGroups();
        } else {
          this.alertService.showError('Error', 'No se pudo crear el grupo');
        }
      },
      error => {
        this.isSaving = false;
        this.alertService.showError('Error', 'Error de conexión');
      }
    );
  }

  // ============ Edit Group ============

  openEditModal(group: SynonymGroup, event: Event): void {
    event.stopPropagation();
    this.editingGroup = { ...group };
    this.showEditModal = true;
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.editingGroup = null;
  }

  updateGroup(): void {
    if (!this.editingGroup || !this.editingGroup.group_name.trim()) {
      this.alertService.showError('Error', 'El nombre del grupo es requerido');
      return;
    }

    this.isSaving = true;
    this.synonymService.saveGroup({
      id: this.editingGroup.id,
      group_name: this.editingGroup.group_name.trim(),
      is_active: this.editingGroup.is_active
    }).subscribe(
      response => {
        this.isSaving = false;
        if (response.success) {
          this.alertService.showSuccess('Éxito', 'Grupo actualizado correctamente');
          this.closeEditModal();
          this.loadGroups();
        } else {
          this.alertService.showError('Error', 'No se pudo actualizar el grupo');
        }
      },
      error => {
        this.isSaving = false;
        this.alertService.showError('Error', 'Error de conexión');
      }
    );
  }

  // ============ Delete Group ============

  openDeleteModal(group: SynonymGroup, event: Event): void {
    event.stopPropagation();
    this.deletingGroup = group;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.deletingGroup = null;
  }

  confirmDeleteGroup(): void {
    if (!this.deletingGroup) return;

    this.isSaving = true;
    this.synonymService.removeGroup(this.deletingGroup.id).subscribe(
      response => {
        this.isSaving = false;
        if (response.success) {
          this.alertService.showSuccess('Éxito', 'Grupo eliminado correctamente');
          this.closeDeleteModal();
          this.loadGroups();
        } else {
          this.alertService.showError('Error', 'No se pudo eliminar el grupo');
        }
      },
      error => {
        this.isSaving = false;
        this.alertService.showError('Error', 'Error de conexión');
      }
    );
  }

  // ============ Toggle Status ============

  toggleStatus(group: SynonymGroup, event: Event): void {
    event.stopPropagation();
    const newStatus = group.is_active === 1 ? 0 : 1;
    
    this.synonymService.saveGroup({
      id: group.id,
      group_name: group.group_name,
      is_active: newStatus
    }).subscribe(
      response => {
        if (response.success) {
          group.is_active = newStatus;
          this.alertService.showSuccess('Éxito', 
            newStatus === 1 ? 'Grupo activado' : 'Grupo desactivado');
        } else {
          this.alertService.showError('Error', 'No se pudo cambiar el estado');
        }
      },
      error => {
        this.alertService.showError('Error', 'Error de conexión');
      }
    );
  }

  // ============ Expand/Collapse Card ============

  toggleExpand(group: SynonymGroup): void {
    this.expandedGroups[group.id] = !this.expandedGroups[group.id];
    if (!this.newSynonymPhrase[group.id]) {
      this.newSynonymPhrase[group.id] = '';
    }
  }

  isExpanded(group: SynonymGroup): boolean {
    return !!this.expandedGroups[group.id];
  }

  // ============ Synonym Management ============

  addSynonym(group: SynonymGroup): void {
    const phrase = this.newSynonymPhrase[group.id]?.trim();
    if (!phrase) {
      this.alertService.showError('Error', 'Ingrese un sinónimo');
      return;
    }

    this.addingSynonym[group.id] = true;
    this.synonymService.saveSynonym({
      group_id: group.id,
      synonym_phrase: phrase
    }).subscribe(
      response => {
        this.addingSynonym[group.id] = false;
        if (response.success) {
          // Add to local array
          if (!group.synonyms) group.synonyms = [];
          group.synonyms.push(response.response);
          this.newSynonymPhrase[group.id] = '';
          this.alertService.showSuccess('Éxito', 'Sinónimo agregado');
        } else {
          this.alertService.showError('Error', 'No se pudo agregar el sinónimo');
        }
      },
      error => {
        this.addingSynonym[group.id] = false;
        this.alertService.showError('Error', 'Error de conexión');
      }
    );
  }

  removeSynonym(group: SynonymGroup, synonym: Synonym, event: Event): void {
    event.stopPropagation();
    
    this.synonymService.removeSynonym(synonym.id).subscribe(
      response => {
        if (response.success) {
          // Remove from local array
          if (group.synonyms) {
            group.synonyms = group.synonyms.filter(s => s.id !== synonym.id);
          }
          this.alertService.showSuccess('Éxito', 'Sinónimo eliminado');
        } else {
          this.alertService.showError('Error', 'No se pudo eliminar el sinónimo');
        }
      },
      error => {
        this.alertService.showError('Error', 'Error de conexión');
      }
    );
  }

  // ============ Pagination ============

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadGroups();
  }

  get totalPages(): number {
    return this.dataItems.last_page || 1;
  }

  get pages(): number[] {
    const total = this.totalPages;
    const current = this.currentPage;
    const pages: number[] = [];
    
    let start = Math.max(1, current - 2);
    let end = Math.min(total, current + 2);
    
    if (current <= 3) {
      end = Math.min(5, total);
    }
    if (current >= total - 2) {
      start = Math.max(1, total - 4);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  getSynonymCount(group: SynonymGroup): number {
    return group.synonyms?.length || 0;
  }
}
